
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import axios from "axios";
import { ListDataSource } from "@/data/List/ListDataSource";
import { ObjectDataSource } from "@/data/Object/ObjecDatatSource";
import { Filter } from "@/cms-services/filter";
import draggable from "vuedraggable";
import stringEditor from "@/components/common/inline-editor/string.vue";
import QuizQuestions from "./quiz-questions.vue";

@Component({
  components: {
    draggable,
    stringEditor,
    QuizQuestions,
  },
})
export default class QuizManager extends Vue {
  @Prop({ required: true }) courseItemGroupId!: number;

  loading = false;
  adding = false;
  expandedQuizId: number | null = null;
  $message: any;

  importDialog = false;
  importFile: File | null = null;
  importJson = "";
  importError = "";
  importing = false;

  quizDataSource: ListDataSource = new ListDataSource({
    className: "quiz",
    config: {
      pageIndex: 1,
      pageSize: 100,
    },
  });

  get quizzes() {
    return this.quizDataSource.items || [];
  }

  @Watch("courseItemGroupId", { immediate: true })
  onCourseItemGroupIdChange() {
    this.loadQuizzes();
  }

  async loadQuizzes() {
    if (!this.courseItemGroupId) return;
    
    this.loading = true;
    try {
      this.quizDataSource.config.filter = JSON.stringify([
        new Filter("courseItemGroupId", this.courseItemGroupId),
      ]);
      await this.quizDataSource.get();
    } catch (e) {
      console.error("Ошибка загрузки тестов:", e);
    } finally {
      this.loading = false;
    }
  }

  async refresh() {
    await this.loadQuizzes();
  }

  getQuizDataSource(quiz: any) {
    return new ObjectDataSource({
      id: quiz.id,
      model: quiz,
      className: "quiz",
      config: {},
    });
  }

  async addQuiz() {
    this.adding = true;
    try {
      const newQuiz = {
        caption: `Новый тест ${this.quizzes.length + 1}`,
        courseItemGroupId: this.courseItemGroupId,
        instanceStateId: 4,
        priority: this.quizzes.length + 1,
      };
      const created = await this.quizDataSource.add(newQuiz, true);
      this.expandedQuizId = created.id;
      this.$message?.("Тест создан");
    } catch (e) {
      console.error("Ошибка создания теста:", e);
      this.$message?.("Ошибка создания теста");
    } finally {
      this.adding = false;
    }
  }

  async removeQuiz(quiz: any) {
    if (!confirm("Удалить тест?")) return;
    
    try {
      await this.quizDataSource.remove(quiz.id);
      if (this.expandedQuizId === quiz.id) {
        this.expandedQuizId = null;
      }
      this.$message?.("Тест удален");
    } catch (e) {
      console.error("Ошибка удаления теста:", e);
      this.$message?.("Ошибка удаления теста");
    }
  }

  toggleQuiz(quizId: number) {
    this.expandedQuizId = this.expandedQuizId === quizId ? null : quizId;
  }

  async onDragEnd() {
    // Обновляем приоритеты после перетаскивания
    const updatePromises = this.quizzes.map((quiz: any, index: number) => {
      return this.getQuizDataSource(quiz).updateField({
        fieldName: "priority",
        fieldValue: index + 1,
      });
    });

    try {
      await Promise.all(updatePromises);
    } catch (e) {
      console.error("Ошибка обновления приоритетов:", e);
    }
  }

  // === Методы импорта ===

  openImportDialog() {
    this.importDialog = true;
    this.importFile = null;
    this.importJson = "";
    this.importError = "";
  }

  onFileSelected() {
    if (!this.importFile) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      this.importJson = e.target?.result as string;
    };
    reader.readAsText(this.importFile);
  }

  async importQuiz() {
    this.importError = "";

    if (!this.importJson.trim()) {
      this.importError = "Введите JSON";
      return;
    }

    let data: any;
    try {
      data = JSON.parse(this.importJson);
    } catch (e) {
      this.importError = "Некорректный JSON формат";
      return;
    }

    if (!data.caption) {
      this.importError = 'JSON должен содержать поле "caption"';
      return;
    }

    this.importing = true;
    try {
      const response = await axios.post(
          `manage/quiz/import/${this.courseItemGroupId}`,
          data
      );

      this.$message?.("Тест успешно импортирован");
      this.importDialog = false;
      await this.refresh();

      // Раскрываем созданный тест
      if (response.data?.id) {
        this.expandedQuizId = response.data.id;
      }
    } catch (e: any) {
      console.error("Ошибка импорта:", e);
      this.importError = e.response?.data || "Ошибка импорта теста";
    } finally {
      this.importing = false;
    }
  }
}
